import React from 'react';
import { useTheme } from 'next-themes';
import { LightThemeLottie, DarkThemeLottie } from '@whelk/components';
import { isMiniProgram } from '@whelk/utils';
import Dropdown from '@whelk/components/src/Base/Dropdown';
import { useTranslation } from 'react-i18next';

const LightIcon = () => (
  <svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M14 17.9375C16.1746 17.9375 17.9375 16.1746 17.9375 14C17.9375 11.8254 16.1746 10.0625 14 10.0625C11.8254 10.0625 10.0625 11.8254 10.0625 14C10.0625 16.1746 11.8254 17.9375 14 17.9375Z'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
    <path d='M14 6.125V7.4375' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
    <path d='M14 20.5625V21.875' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
    <path
      d='M21.875 14L20.5625 14'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
    <path d='M7.4375 14L6.125 14' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
    <path
      d='M19.5679 19.5679L18.6398 18.6398'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
    <path
      d='M9.3584 9.35925L8.43032 8.43118'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
    <path
      d='M8.43213 19.5687L9.36021 18.6406'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
    <path
      d='M18.6416 9.35925L19.5697 8.43118'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
    />
  </svg>
);

const DarkIcon = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M22.3815 16.6121C22.7094 17.1753 22.6973 17.8741 22.3499 18.4255C20.8813 20.7569 18.2792 22.3123 15.3125 22.3123C10.7216 22.3123 7 18.5907 7 13.9998C7 9.53867 10.5135 5.89916 14.9254 5.69619C15.5764 5.66624 16.1901 6.00053 16.5181 6.56371C16.846 7.12689 16.8339 7.82566 16.4865 8.37708C16.0201 9.11751 15.75 9.99319 15.75 10.9373C15.75 13.5952 17.9046 15.7498 20.5625 15.7498C20.6385 15.7498 20.7139 15.7481 20.7888 15.7446C21.4398 15.7147 22.0535 16.049 22.3815 16.6121ZM19.3625 19.1638C19.8139 18.8094 20.2176 18.397 20.5625 17.9379C20.6706 17.794 20.773 17.6455 20.8692 17.4928C20.7676 17.4974 20.6653 17.4998 20.5625 17.4998C19.9671 17.4998 19.3901 17.4205 18.8417 17.2719C16.052 16.5159 14 13.9662 14 10.9373C14 10.3777 14.07 9.83449 14.2018 9.31591C14.3724 8.64479 14.6464 8.01494 15.0058 7.44434C14.2316 7.47996 13.4923 7.64975 12.8108 7.93099C10.4274 8.91455 8.75 11.2613 8.75 13.9998C8.75 17.6242 11.6881 20.5623 15.3125 20.5623C16.8408 20.5623 18.2471 20.0399 19.3625 19.1638Z'
      fill='currentColor'
    />
  </svg>
);

const SystemIcon = () => (
  <svg width='28' height='28' viewBox='0 0 28 28' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M20.8324 7.44318H7.16769C6.85324 7.44318 6.59833 7.68033 6.59833 7.97286V16.4478C6.59833 16.7403 6.85324 16.9775 7.16769 16.9775H20.8324C21.1468 16.9775 21.4018 16.7403 21.4018 16.4478V7.97286C21.4018 7.68033 21.1468 7.44318 20.8324 7.44318Z'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M14 17.1319V20.2983'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M10.4207 20.7112H17.5793'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ThemeToggle: React.FC = () => {
  const { setTheme, resolvedTheme, theme } = useTheme();
  const { t } = useTranslation();

  const handleMenuClick = (key: string) => {
    console.log('key', key);
    setTheme(key);
  };

  if (isMiniProgram) {
    return null;
  }
  const systemColor = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

  const themeList = [
    { label: t('light_mode'), key: 'light', icon: <LightIcon /> },
    { label: t('dark_mode'), key: 'dark', icon: <DarkIcon /> },
    {
      label: (
        <div className='flex items-center gap-1'>
          {t('system_mode')}
          <span className='text-col_text02 font-[400]'>{systemColor}</span>
        </div>
      ),
      key: 'system',
      icon: <SystemIcon />,
    },
  ];

  return (
    <Dropdown
      placement='bottomLeft'
      menu={themeList.map((item) => ({
        ...item,
        active: item.key === theme,
        onClick: () => handleMenuClick(item.key),
      }))}
    >
      <div className='md:hover:bg-col_bg03 flex h-8 w-8 cursor-pointer items-center justify-center rounded-[8px]'>
        {resolvedTheme === 'light' ? <LightThemeLottie /> : <DarkThemeLottie />}
        {/* <span className='ml-2 hidden md:inline-block text-sm'>{getCurrentThemeLabel()}</span> */}
      </div>
    </Dropdown>
  );
};

export default ThemeToggle;
